import React from 'react';
import { motion } from 'framer-motion';
import { 
  Bot, 
  Brain, 
  Zap, 
  MessageSquare, 
  Search, 
  Code,
  BarChart,
  ShieldCheck,
  Users,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Image,
  Trophy
} from 'lucide-react';
import { Link } from 'react-router-dom';

const features = [
  {
    icon: Bot,
    title: 'AI Spjallmenni',
    description: 'Sérsniðin spjallmenni fyrir þjónustuver og vefverslanir.'
  },
  {
    icon: Brain,
    title: 'Sjálfvirk greining',
    description: 'Gervigreind fyrir gagnagreiningu og spár.'
  },
  {
    icon: MessageSquare,
    title: 'Textavinnsla',
    description: 'Sjálfvirk textavinnsla og málskilningur.'
  },
  {
    icon: Search,
    title: 'Leitarvélabestun',
    description: 'AI-knúin leitarvélabestun fyrir betri árangur.'
  },
  {
    icon: Code,
    title: 'Sérsniðnar lausnir',
    description: 'AI lausnir sérsniðnar að þínum þörfum.'
  },
  {
    icon: Image,
    title: 'AI Myndgreining',
    description: 'Sjálfvirk myndgreining og flokkun.'
  }
];

const aiSolutions = [
  {
    icon: Bot,
    title: 'AI Þjónustuver',
    description: 'Sjálfvirkt þjónustuver með gervigreind',
    features: [
      'Svarar algengum spurningum',
      'Vinnur allan sólarhringinn',
      'Lærir af samskiptum',
      'Tengist þínum kerfum',
      'Fjölmála stuðningur',
      'Persónuleg svör',
      'Greining á samskiptum'
    ]
  },
  {
    icon: Brain,
    title: 'AI Gagnagreining',
    description: 'Greining og spár með gervigreind',
    features: [
      'Sölugreiningar og spár',
      'Viðskiptavinaskilningur',
      'Markaðsgreiningar',
      'Áhættumat',
      'Sjálfvirkar skýrslur',
      'Mynstursgreining',
      'Ákvarðanataka'
    ]
  },
  {
    icon: MessageSquare,
    title: 'AI Textavinnsla',
    description: 'Sjálfvirk textavinnsla og efnisgerð',
    features: [
      'Efnisgerð fyrir vefi',
      'Þýðingar',
      'Samantektir',
      'Málfarsleiðréttingar',
      'SEO bestun',
      'Persónumiðað efni',
      'Sjálfvirk skjöl'
    ]
  }
];

const benefits = [
  {
    icon: Zap,
    title: 'Aukin skilvirkni',
    description: 'Sjálfvirkni og hraðari vinnsla með gervigreind.'
  },
  {
    icon: ShieldCheck,
    title: 'Áreiðanleiki',
    description: 'Nákvæm og áreiðanleg vinnsla allan sólarhringinn.'
  },
  {
    icon: Users,
    title: 'Betri þjónusta',
    description: 'Persónulegri og skilvirkari þjónusta við viðskiptavini.'
  },
  {
    icon: BarChart,
    title: 'Gagnadrifnar ákvarðanir',
    description: 'Betri ákvarðanir byggðar á AI greiningu.'
  },
  {
    icon: Sparkles,
    title: 'Nýsköpun',
    description: 'Nýjar lausnir og tækifæri með gervigreind.'
  },
  {
    icon: Trophy,
    title: 'Samkeppnisforskot',
    description: 'Forskot á samkeppnisaðila með nýjustu tækni.'
  }
];

const AI = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              Gervigreindarlausnir fyrir framtíðina
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Við hjálpum fyrirtækjum að nýta sér kraft gervigreindar til að auka 
              skilvirkni, bæta þjónustu og skapa ný tækifæri.
            </p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/fa-tilbod">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-[#EA295A] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#d01d4c] transition-all shadow-lg shadow-[#EA295A]/20 hover:shadow-xl hover:shadow-[#EA295A]/30 flex items-center"
                >
                  Fá tilboð í AI lausn
                  <ArrowRight className="ml-2" />
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <motion.div 
        className="py-24 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              AI lausnir sem skila árangri
            </h2>
            <p className="text-xl text-gray-600">
              Við bjóðum upp á fjölbreyttar gervigreindarlausnir fyrir fyrirtæki
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="bg-gray-50 rounded-xl p-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                  <feature.icon className="w-6 h-6 text-[#EA295A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Solutions Section */}
      <motion.div 
        className="py-24 bg-gray-50"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Sérsniðnar AI lausnir
            </h2>
            <p className="text-xl text-gray-600">
              Gervigreindarlausnir sérsniðnar að þínum þörfum
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {aiSolutions.map((solution, index) => (
              <motion.div 
                key={index}
                className="bg-white rounded-xl p-8 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                  <solution.icon className="w-6 h-6 text-[#EA295A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{solution.title}</h3>
                <p className="text-gray-600 mb-6">{solution.description}</p>
                <ul className="space-y-3 mb-8">
                  {solution.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center text-gray-600">
                      <CheckCircle2 className="w-5 h-5 text-[#EA295A] mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/fa-tilbod">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-[#EA295A] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#d01d4c] transition-all"
                  >
                    Fá tilboð
                  </motion.button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Benefits Section */}
      <motion.div 
        className="py-24 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Ávinningur af gervigreind
            </h2>
            <p className="text-xl text-gray-600">
              Kynntu þér helstu kosti þess að nýta gervigreind í þínum rekstri
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div 
                key={index}
                className="bg-gray-50 rounded-xl p-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                  <benefit.icon className="w-6 h-6 text-[#EA295A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* CTA Section */}
      <motion.div 
        className="py-24 bg-gray-50"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#EA295A] to-[#FF6B6B] rounded-2xl p-12 text-center">
            <motion.h2 
              className="text-3xl font-bold text-white mb-6"
            >
              Tilbúin að nýta kraft gervigreindar?
            </motion.h2>
            <motion.p 
              className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"
            >
              Láttu okkur hjálpa þér að innleiða gervigreindarlausnir sem skila 
              árangri
            </motion.p>
            <motion.div>
              <Link to="/fa-tilbod">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-white text-[#EA295A] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-lg"
                >
                  Fá tilboð í AI lausn
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AI;